import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { LuNavigation, LuCheck, LuChevronRight, LuWand, LuArrowUpDown } from 'react-icons/lu';
import OrdenarRestoSheet from '../ordenes/OrdenarRestoSheet';
import VolverDeposito from '../mapa/VolverDeposito';
import { fueOtraCosa, corto } from '../../utils/pedidoVsHecho';
import { colorTecnico } from '../../utils/estados';
import { claveAgenda, horaTexto } from '../../utils/ordenes';
import { getTodayISO, fechaAR } from '../../utils/dateUtils';
import { linkRuta, linkWaze } from '../mapa/MapaLeaflet';
import AvatarTecnico from '../ui/AvatarTecnico';

// Recorrido de hoy en el Inicio (27-oct-2026). Reemplaza "Ruta de hoy" + la agenda por franjas: el Inicio es
// el día de hoy y cada visita de hoy está acá y en ningún otro lado. Una tarjeta por técnico con su avance
// y sus paradas en orden: las hechas con la hora en que se cerraron, la que sigue marcada y las que faltan.
// Tocar una parada abre su ficha. Abajo, Google Maps con las que faltan.
const VAN = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO', 'COMPLETADA', 'NO_ATENDIDO'];
const FALTAN = ['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'];
const EN_CURSO = { EN_CAMINO: 'en camino', EN_SITIO: 'trabajando' };
const calle = (d) => String(d || '').split(',')[0].trim();
const horaCierre = (iso) => { const m = String(iso || '').match(/T(\d{2}:\d{2})/); return m ? m[1] : null; };
const finDeFranja = (h) => { const m = String(h || '').match(/-(\d{1,2}):?(\d{2})?/); return m ? `${Number(m[1])} h` : null; };

function Parada({ o, sigue, onAbrir }) {
    const hecha = o.estado === 'COMPLETADA';
    const noAtendida = o.estado === 'NO_ATENDIDO';
    const quien = o.lugar || o.clienteNombre || o.titulo || `Visita #${o.id}`;
    // Hecha: lo que se hizo; si fue otra cosa que lo pedido, se marca (28-oct-2026)
    const otra = hecha && fueOtraCosa(o.motivo, o.hecho);
    const trabajo = hecha && o.hecho ? corto(o.hecho) : (o.motivo || (o.lugar ? null : o.titulo !== quien ? o.titulo : null));
    const detalle = [trabajo, calle(o.direccion),
        EN_CURSO[o.estado], sigue && !EN_CURSO[o.estado] ? 'sigue' : null, noAtendida ? 'no atendida' : null].filter(Boolean).join(' · ');
    const hora = hecha ? (horaCierre(o.fechaCompletada) || '✓') : (horaTexto(o.horaEstimada) || 'Sin hora');
    return (
        <button type="button" onClick={() => onAbrir(o)}
            className={`w-full flex items-start gap-2.5 py-2.5 text-left active:opacity-70 ${sigue ? 'bg-[rgba(201,52,31,0.12)] -mx-2 px-2 rounded-xl' : 'border-t border-black/[0.06] dark:border-white/[0.06] first:border-t-0'}`}>
            <span className={`w-14 shrink-0 pt-0.5 text-caption font-black ${sigue ? 'text-brand-red' : 'text-muted'}`}>{hora}</span>
            <span className={`w-5 h-5 mt-0.5 shrink-0 rounded-full inline-flex items-center justify-center ${hecha
                ? 'bg-[var(--etapa-listo-bg)] text-[var(--etapa-listo-tx)]' : sigue ? 'bg-brand-red text-white'
                : noAtendida ? 'bg-[var(--alerta-bg)] text-[var(--alerta-tx)]' : 'border-2 border-black/20 dark:border-white/25'}`}>
                {hecha ? <LuCheck size={12} strokeWidth={3} /> : sigue ? <LuChevronRight size={13} strokeWidth={3} /> : null}
            </span>
            <span className="flex-1 min-w-0">
                <span className={`block text-body-lg font-black truncate ${hecha ? 'text-secondary' : 'text-ink'}`}>{quien}</span>
                {detalle && <span className="block text-body text-muted truncate">{detalle}</span>}
                {otra && <span className="mt-1 inline-flex h-6 px-2 rounded-md text-caption font-black items-center bg-[rgba(217,119,6,0.15)] text-[#B45309] dark:text-[#F0A500]">Pidieron {String(o.motivo).toLowerCase()}</span>}
            </span>
        </button>
    );
}

export default function RecorridoHoy({ ordenes, cargando, onAbrir, onArmar, onCambio }) {
    const hoy = getTodayISO();
    const [ordenar, setOrdenar] = useState(null);   // técnico: "Ordenar lo que falta" (28-oct-2026)
    // 28-oct-2026: los técnicos sin visitas hoy también tienen su tarjeta (antes Marcos no aparecía y quedaba un hueco)
    const [todos, setTodos] = useState([]);
    useEffect(() => { api.get('/ordenes/tecnicos').then(r => setTodos(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
    const tecnicos = useMemo(() => {
        const por = new Map();
        ordenes.filter(o => VAN.includes(o.estado) && String(o.fechaProgramada || '').slice(0, 10) === hoy)
            .sort((a, b) => {
                // Lo hecho por la hora en que se cerró; lo que falta, por su franja
                const ka = a.estado === 'COMPLETADA' ? `0${a.fechaCompletada || ''}` : `1${claveAgenda(a.horaEstimada, a.ordenDia, a.id)}`;
                const kb = b.estado === 'COMPLETADA' ? `0${b.fechaCompletada || ''}` : `1${claveAgenda(b.horaEstimada, b.ordenDia, b.id)}`;
                return ka.localeCompare(kb);
            })
            .forEach(o => { const k = o.tecnicoId; if (!por.has(k)) por.set(k, { id: k, nombre: o.tecnicoNombre, visitas: [] }); por.get(k).visitas.push(o); });
        const lista = [...por.values()];
        todos.filter(t => t.activo !== false && !por.has(t.id)).forEach(t => {
            // Su próxima visita, para que se vea cuándo vuelve a tener trabajo
            const prox = ordenes.filter(o => o.tecnicoId === t.id && o.estado === 'PENDIENTE' && String(o.fechaProgramada || '').slice(0, 10) > hoy)
                .sort((a, b) => String(a.fechaProgramada).localeCompare(String(b.fechaProgramada)) || claveAgenda(a.horaEstimada, a.ordenDia, a.id).localeCompare(claveAgenda(b.horaEstimada, b.ordenDia, b.id)))[0];
            lista.push({ id: t.id, nombre: t.nombre, visitas: [], proxima: prox || null });
        });
        return lista;
    }, [ordenes, hoy, todos]);

    if (cargando && !ordenes.length) return <div className="h-40 rounded-2xl bg-card animate-pulse" />;
    if (!tecnicos.some(t => t.visitas.length) && !tecnicos.length) return (
        <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-4 flex items-center gap-3">
            <p className="flex-1 text-body text-secondary">Hoy no hay visitas agendadas.</p>
            <button type="button" onClick={onArmar} className="h-11 px-4 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center gap-1.5 active:scale-95">
                <LuWand size={15} /> Armar día
            </button>
        </div>
    );
    return (
        <div className="grid gap-2 md:grid-cols-2 items-start">
            {tecnicos.map(t => {
                if (!t.visitas.length) {
                    const p = t.proxima;
                    return (
                        <div key={t.id} className="min-w-0 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-3.5">
                            <div className="flex items-center gap-2.5">
                                <AvatarTecnico nombre={t.nombre} size={32} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-body-lg font-black text-ink">{String(t.nombre || 'Técnico').split(' ')[0]}</p>
                                    <p className="text-caption text-muted">Sin visitas hoy</p>
                                </div>
                            </div>
                            <div className="h-2 my-3 rounded-full bg-chip" />
                            {p ? (
                                <button type="button" onClick={() => onAbrir(p)} className="w-full flex items-start gap-2.5 py-1 text-left active:opacity-70">
                                    <span className="w-14 shrink-0 pt-0.5 text-caption font-black text-muted">Próxima</span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-body-lg font-black text-ink truncate">{p.lugar || p.clienteNombre || p.titulo}</span>
                                        <span className="block text-body text-muted truncate">{[fechaAR(p.fechaProgramada), horaTexto(p.horaEstimada), calle(p.direccion)].filter(Boolean).join(' · ')}</span>
                                    </span>
                                </button>
                            ) : <p className="text-body text-muted">No tiene visitas agendadas.</p>}
                            <button type="button" onClick={onArmar}
                                className="mt-3 w-full h-11 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                <LuWand size={15} /> Armarle un día
                            </button>
                        </div>
                    );
                }
                const faltan = t.visitas.filter(o => FALTAN.includes(o.estado));
                const hechas = t.visitas.filter(o => o.estado === 'COMPLETADA').length;
                const sigueId = faltan[0]?.id;
                const url = faltan.length ? linkRuta(faltan.map(o => ({ direccion: o.direccion }))) : null;
                const ultima = faltan.length ? finDeFranja(faltan[faltan.length - 1].horaEstimada) : null;
                const pct = Math.round((hechas / Math.max(t.visitas.length, 1)) * 100);
                return (
                    <div key={t.id} className="min-w-0 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-3.5">
                        <div className="flex items-center gap-2.5">
                            <AvatarTecnico nombre={t.nombre} size={32} />
                            <div className="flex-1 min-w-0">
                                <p className="text-body-lg font-black text-ink">{String(t.nombre || 'Técnico').split(' ')[0]}</p>
                                <p className="text-caption text-muted">
                                    {hechas} de {t.visitas.length} hecha{t.visitas.length !== 1 ? 's' : ''}
                                    {faltan.length ? (ultima ? ` · termina ${ultima}` : '') : ' · terminó el día'}
                                </p>
                            </div>
                        </div>
                        <div className="h-2 my-3 rounded-full bg-chip overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--etapa-listo)' : colorTecnico(t.nombre) }} />
                        </div>
                        <div>
                            {t.visitas.map(o => <Parada key={o.id} o={o} sigue={o.id === sigueId} onAbrir={onAbrir} />)}
                        </div>
                        {url && (
                            <div className="mt-3 space-y-2">
                                <div className="flex gap-2">
                                    <a href={url} target="_blank" rel="noreferrer" data-sin-elegir
                                        className="flex-1 h-11 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                        <LuNavigation size={15} /> Google Maps{faltan.length < t.visitas.length ? ` (${faltan.length})` : ''}
                                    </a>
                                    {/* Waze lleva a una parada por vez: la que sigue (29-oct-2026) */}
                                    <a href={linkWaze(faltan[0].direccion)} target="_blank" rel="noreferrer"
                                        className="flex-1 h-11 rounded-xl bg-[#33CCFF] text-[#0B2A3A] text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                        <LuNavigation size={15} /> Waze · próxima
                                    </a>
                                </div>
                                <div className="flex gap-2">
                                    {faltan.filter(o => o.estado === 'PENDIENTE').length > 1 && (
                                        <button type="button" onClick={() => setOrdenar(t)}
                                            className="flex-1 h-11 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                            <LuArrowUpDown size={15} /> Ordenar
                                        </button>
                                    )}
                                    {/* 29-oct-2026: solo en la tarjeta de Lucas (el que sale del depósito); Marcos sale de Lanús */}
                                    {/lucas/i.test(String(t.nombre || '')) && <VolverDeposito className="flex-1 h-11 justify-center" />}
                                </div>
                            </div>
                        )}

                    </div>
                );
            })}
            {ordenar && <OrdenarRestoSheet tecnicoId={ordenar.id} tecnicoNombre={ordenar.nombre || 'Técnico'}
                onCerrar={() => setOrdenar(null)} onListo={() => { setOrdenar(null); onCambio?.(); }} />}
        </div>
    );
}
